import React, { useState } from "react"
import MyHeader from "../Header/Header"
import Navbar from "./Navbar"
import menusStatic from "./menus"
import { resolveMenuIcon } from "./menuIcons"
import "./index.css"
import { useStore } from "../../ui"

/** Mục đã gom vào nhóm static — bỏ bản top-level cũ từ API để tránh trùng menu. */
const ORPHAN_MENU_CODES = new Set([
  "ALERT_CONTROLLER",
  "INCIDENT_CONTROLLER",
  "AUDIT_CONTROLLER",
  "AMM_OVERVIEW",
  "AMM_EQUIPMENT",
  "AMM_STOCK",
  "AMM_PROJECT_STOCK",
  "AMM_STOCKTAKE",
  "AMM_ISSUE",
  "AMM_MAINT",
  "AMM_SETTINGS",
  "AMM_REPORTS"
])

/** Gộp menu API với menu tĩnh — giữ thứ tự static; bổ sung mục chỉ có ở API. */
function mergeMenusWithStatic(apiMenus, staticMenus) {
  if (!Array.isArray(apiMenus) || apiMenus.length === 0) return staticMenus
  const enriched = apiMenus.map((apiItem) => {
    const staticItem = staticMenus.find((s) => s.code === apiItem.code)
    if (!staticItem) {
      return { ...apiItem, iconKey: apiItem.iconKey || apiItem.icon }
    }
    return {
      ...apiItem,
      iconKey: staticItem.iconKey || apiItem.iconKey || apiItem.icon,
      tab: (staticItem.tab?.length ? staticItem.tab : apiItem.tab || []).map((t) => {
        const st = (staticItem.tab || []).find((x) => x.code === t.code || x.url === t.url)
        return {
          ...t,
          iconKey: st?.iconKey || t.iconKey || t.icon,
          fullPath: st?.fullPath ?? t.fullPath
        }
      })
    }
  })
  const byCode = new Map(enriched.map((m) => [m.code, m]))
  const ordered = staticMenus.map((s) => byCode.get(s.code) || s)
  for (const m of enriched) {
    if (staticMenus.some((s) => s.code === m.code)) continue
    if (ORPHAN_MENU_CODES.has(m.code)) continue
    ordered.push(m)
  }
  return ordered
}

function hasPermission(permissions, required) {
  if (!required) return true
  return Array.isArray(permissions) && permissions.includes(required)
}

function convertMenus(menus, permissions) {
  if (!menus?.length) return []

  const items = menus
    .map((i) => {
      const __children = []
      const subItems = i.tab || i.sub_menu || []
      const baseUrl = i.url ? i.url.replace(/\/[^/]+$/, "") : ""
      for (let index = 0; index < subItems.length; index++) {
        const t = subItems[index]
        if (!hasPermission(permissions, t.permission_required)) continue
        const raw = String(t.url || "")
        const segments = raw.split("/").filter(Boolean)
        let path
        if (t.fullPath) {
          path = raw.startsWith("/") ? raw : `/${raw}`
        } else if (raw.startsWith("/")) {
          path = segments.length <= 1 && baseUrl ? baseUrl + raw : raw
        } else {
          path = `${baseUrl || i.url || ""}/${raw}`
        }
        __children.push({
          href: path,
          label: t.title || t.label || "",
          icon: resolveMenuIcon(t.iconKey)
        })
      }

      const hasChildren = subItems.length > 0
      const parentAllowed = hasChildren
        ? __children.length > 0
        : hasPermission(permissions, i.permission_required)

      if (!parentAllowed) return null

      return {
        href: i.url,
        label: i.title,
        icon: resolveMenuIcon(i.iconKey),
        children: __children
      }
    })
    .filter(Boolean)

  return items
}

function MyLayout({ children }) {
  const { user, menus: menusFromApi, permissions } = useStore()
  const isShow = localStorage.getItem("isShow")
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768
  const [isNav, setIsNav] = useState(isMobile ? false : isShow !== "false")
  const menus =
    Array.isArray(menusFromApi) && menusFromApi.length > 0
      ? mergeMenusWithStatic(menusFromApi, menusStatic)
      : menusStatic

  function toggleSidebar() {
    localStorage.setItem("isShow", !isNav)
    setIsNav((c) => !c)
  }

  return (
    <div className={`ees-shell ${isMobile ? "min-h-screen relative" : "flex min-h-screen"}`}>
      <Navbar items={convertMenus(menus, permissions)} isNav={isNav} isMobile={isMobile} onClose={toggleSidebar} />
      {isMobile && isNav && (
        <div
          className="fixed inset-0 bg-black/35 z-[5] backdrop-blur-[1px]"
          onClick={(e) => {
            if (e.target === e.currentTarget) toggleSidebar()
          }}
          role="button"
          tabIndex={0}
          aria-label="Close sidebar"
        />
      )}
      <section className={`${isMobile ? "min-w-0 bg_layout_content" : "flex-grow min-w-0 bg_layout_content"}`}>
        <header
          className={`ees-topbar flex justify-between items-center py-2 pr-6 pl-4 h-14 w-full ${
            isMobile ? "z-[50]" : "sticky top-0 z-[50]"
          }`}
        >
          <button onClick={toggleSidebar} className="ees-icon-btn text-slate-100 rounded-lg" aria-label="Toggle menu">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h8m-8 6h16" />
            </svg>
          </button>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs tracking-wide text-teal-100/80">EES eZlog 2.0</span>
            <MyHeader user={user} />
          </div>
        </header>
        <div className="ees-page p-4 md:p-6">{children}</div>
      </section>
    </div>
  )
}

export default MyLayout
